/**
 * Scroll utility providing optional momentum-style scrolling for horizontal (desktop)
 * and vertical (mobile) page navigation.
 *
 * Variables / properties:
 * - momentum: Active momentum axis controller (null when using native scroll only).
 * - enableMobileMomentum: Flag to enable experimental momentum on mobile (off by default).
 * Methods:
 * - init(onFrame): Initializes momentum handlers; onFrame called every animation frame during inertia.
 * - getScrollPos(): Current scroll position from momentum (0 if inactive).
 * - getScrollVelocity(): Current velocity applied by momentum (0 if inactive).
 * - setMobileMomentumEnabled(enabled): Toggles mobile momentum and re-initializes.
 */
let scroll = {
  momentum: null, // { push, getPos, getMax, getVelocity } returned by createMomentumAxis
  enableMobileMomentum: false, // whether vertical momentum (touch/wheel) is active on mobile
  _mode: null, // 'desktop' | 'mobile-momentum' | 'mobile-native'
  _container: null,
  _resizeBound: false,
  _listenerCleanups: [], // functions to remove bound event listeners
  getCurrentPageIndex: function() {
    const container = this._container || document.querySelector('.pages-container');
    if (!container) return 0;
    const pages = Array.from(document.querySelectorAll('.page'));
    if (!pages.length) return 0;
    if (this._mode === 'desktop') {
      const scrollPos = container.scrollLeft;
      let bestIndex = 0;
      let bestDist = Infinity;
      pages.forEach((p,i) => {
        const left = p.offsetLeft;
        const dist = Math.abs(left - scrollPos);
        if (dist < bestDist) { bestDist = dist; bestIndex = i; }
      });
      return bestIndex;
    } else {
      const scrollPos = container.scrollTop;
      let bestIndex = 0;
      let bestDist = Infinity;
      pages.forEach((p,i) => {
        const top = p.offsetTop;
        const dist = Math.abs(top - scrollPos);
        if (dist < bestDist) { bestDist = dist; bestIndex = i; }
      });
      return bestIndex;
    }
  },
  scrollToPageIndex: function(index) {
    const container = this._container || document.querySelector('.pages-container');
    if (!container) return;
    const pages = Array.from(document.querySelectorAll('.page'));
    if (!pages.length) return;
    const target = pages[Math.min(Math.max(index,0), pages.length-1)];
    if (!target) return;
    if (this._mode === 'desktop') {
      container.scrollLeft = target.offsetLeft;
    } else {
      container.scrollTop = target.offsetTop;
    }
  },
  _normalizeImages: function() {
    const container = this._container || document.querySelector('.pages-container');
    if (!container) return;
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const targetHeight = container.clientHeight; // consistent reference height
    const images = container.querySelectorAll('.page-image');
    images.forEach(img => {
      // Skip if image not loaded yet to avoid forcing incorrect aspect ratio; we'll catch it on next pass
      if (!img.complete || !img.naturalWidth) return;
      if (isDesktop) {
        // Safari flex-direction switching sometimes leaves stale height; force explicit height
        img.style.height = targetHeight + 'px';
        img.style.width = 'auto';
        img.style.objectFit = 'contain';
      } else {
        img.style.width = '100%';
        img.style.height = 'auto';
        img.style.objectFit = 'contain';
      }
    });
  },
  _scheduleNormalize: function() {
    clearTimeout(this._normalizeTimer);
    this._normalizeTimer = setTimeout(() => this._normalizeImages(), 60); // small debounce
  },
  init: function(onFrame, opts) {
    // Guard against rapid re-entrant init calls (resize + mediaQuery change firing together)
    if (this._initInProgress) return;
    this._initInProgress = true;
    const preserveIndex = opts && typeof opts.preserveIndex === 'number' ? opts.preserveIndex : null;
    // Only carry over previous velocity if we stay in same momentum-driven mode
    const previousMode = this._mode;
    let prevVelocity = this.momentum ? this.momentum.getVelocity() : 0;
    const pagesContainer = document.querySelector('.pages-container'); // outer scrolling element
    const pagesInner = document.querySelector('.pages-inner'); // inner content sizing element
    if (!pagesContainer || !pagesInner) return;
    this._container = pagesContainer;

    // Cleanup previously bound listeners to avoid duplicates after re-init.
    if (this._listenerCleanups.length) {
      this._listenerCleanups.forEach(fn => { try { fn(); } catch(_){} });
      this._listenerCleanups = [];
    }
    // Stop previous momentum loop (if any) so its RAF doesn't keep mutating scrollLeft/Top after mode switch
    if (this.momentum && typeof this.momentum.stop === 'function') {
      this.momentum.stop();
    }

  const desktopQuery = window.matchMedia('(min-width: 768px)'); // breakpoint: desktop uses horizontal momentum

    // Factory: builds a momentum controller for one scroll axis
    // Params:
    // - getPos(): current scroll position
    // - setPos(v): apply a scroll position
    // - getMax(): maximum scrollable distance (contentSize - viewportSize)
    // - onEdgeClamp(edge): optional callback when clamped at 'start' or 'end'
    // - friction: decay factor applied each frame to velocity
    // - acceleration: multiplier converting user input delta to velocity
    // - maxVelocity: absolute cap on velocity magnitude
    // - minVelocity: threshold below which animation stops
    const createMomentumAxis = ({
      getPos, setPos, getMax, onEdgeClamp,
      friction = .96, // inertia decay per frame
      acceleration = .09, // scale raw wheel/touch delta -> velocity
      maxVelocity = 100, // clamp high-end velocity (desktop)
      minVelocity = 1, // stop threshold
      initialVelocity = 5
    }) => {
      let velocity = initialVelocity;
      let animating = false; // whether RAF loop is active
      let disposed = false;

      const step = () => {
        if (disposed) { animating = false; velocity = 0; return; }
        if (Math.abs(velocity) > minVelocity) {
          const maxScroll = getMax();
          if (maxScroll <= 0) { velocity = 0; animating = false; return; }

          let next = getPos() + velocity; // proposed next scroll position
          if (next < 0) { next = 0; velocity *= 0.4; onEdgeClamp && onEdgeClamp('start'); }
          else if (next > maxScroll) { next = maxScroll; velocity *= 0.4; onEdgeClamp && onEdgeClamp('end'); }

          setPos(next);
          velocity *= friction;
          if (onFrame) onFrame();
          requestAnimationFrame(step);
        } else {
          velocity = 0;
          animating = false;
        }
      };

      const push = (delta) => {
        if (disposed) return; // ignore pushes after dispose
        velocity += delta * acceleration; // convert user input delta to momentum velocity
        velocity = Math.max(Math.min(velocity, maxVelocity), -maxVelocity); // clamp within +/- maxVelocity
        if (!animating) { animating = true; requestAnimationFrame(step); }
      };

      const stop = () => { disposed = true; velocity = 0; };
      return { push, stop, getPos, getMax, getVelocity: () => velocity };
    };

  // Desktop: horizontal momentum on pagesContainer (wheel vertical mapped to horizontal)
    // Allow query param override: ?mobileMomentum=1 to force enable
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mobileMomentum') === '1') this.enableMobileMomentum = true;
      if (params.get('mobileMomentum') === '0') this.enableMobileMomentum = false;
    } catch(_){}

    // Mode-specific configs for momentum characteristics
    const DESKTOP_CONFIG = {
      friction: 0.96,
      acceleration: 0.09,
      maxVelocity: 100,
      minVelocity: 5 
    };
    const MOBILE_MOMENTUM_CONFIG = {
      friction: 0.96,
      acceleration: 0.09,
      maxVelocity: 100,
      minVelocity: 5};

    if (desktopQuery.matches) {
      this._mode = 'desktop';
      if (previousMode !== this._mode) prevVelocity = 0; // avoid stale velocity influencing new axis
      const iv = Math.max(Math.min(prevVelocity, DESKTOP_CONFIG.maxVelocity), -DESKTOP_CONFIG.maxVelocity);
      this.momentum = createMomentumAxis({
        getPos: () => pagesContainer.scrollLeft,
        setPos: v => { pagesContainer.scrollLeft = v; },
        getMax: () => pagesContainer.scrollWidth - pagesContainer.clientWidth,
        friction: DESKTOP_CONFIG.friction,
        acceleration: DESKTOP_CONFIG.acceleration,
        maxVelocity: DESKTOP_CONFIG.maxVelocity,
        minVelocity: DESKTOP_CONFIG.minVelocity,
        initialVelocity: iv
      });

      const wheelHandler = (e) => {
        if (e.deltaY !== 0) {
          e.preventDefault();
          if (this.momentum && typeof this.momentum.push === 'function') {
            this.momentum.push(e.deltaY);
          }
        }
      };
      pagesContainer.addEventListener('wheel', wheelHandler, { passive: false });
      this._listenerCleanups.push(() => pagesContainer.removeEventListener('wheel', wheelHandler, { passive: false }));

      // Touch / pointer drag support for iPad (and other touch devices that meet desktopQuery)
      // iPad Pro often reports a wide viewport triggering desktop branch, but provides only touch events (no wheel).
      // We map vertical finger movement to horizontal scroll (same mental model as wheel mapping above).
      const isTouchCapable = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
      if (isTouchCapable) {
        let dragging = false;
        let startX = 0;
        let startScrollLeft = 0;
        let lastMoveTime = 0;
        let lastDeltaX = 0; // last movement delta for inertia estimation

        const beginDrag = (clientX) => {
          dragging = true;
          startX = clientX;
          startScrollLeft = pagesContainer.scrollLeft;
          lastMoveTime = performance.now();
          lastDeltaX = 0;
        };
        const moveDrag = (clientX, preventDefaultCb) => {
          if (!dragging) return;
          const deltaX = startX - clientX; // finger moving right -> scroll forward
          pagesContainer.scrollLeft = startScrollLeft + deltaX;
          const now = performance.now();
            // compute velocity: distance since last move (px per frame approximation)
          lastDeltaX = clientX - startX; // direction aware for inertia (reverse sign for momentum mapping)
          lastMoveTime = now;
          preventDefaultCb && preventDefaultCb();
        };
        const endDrag = () => {
          if (!dragging) return;
          dragging = false;
          // Map lastDeltaX to a momentum push; invert sign to match wheel delta semantics
          const inertiaDelta = -(lastDeltaX);
          // Apply a scaled push so inertia feels natural
          if (this.momentum && typeof this.momentum.push === 'function') {
            this.momentum.push(inertiaDelta);
          }
        };

        // Pointer events (covers modern Safari on iPad)
        const pointerDown = (e) => {
          if (e.isPrimary && (e.pointerType === 'touch' || e.pointerType === 'pen')) {
            beginDrag(e.clientX);
          }
        };
        pagesContainer.addEventListener('pointerdown', pointerDown, { passive: true });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('pointerdown', pointerDown, { passive: true }));
        const pointerMove = (e) => {
          if (!dragging) return;
          if (e.isPrimary && (e.pointerType === 'touch' || e.pointerType === 'pen')) {
            moveDrag(e.clientX, () => e.preventDefault());
          }
        };
        pagesContainer.addEventListener('pointermove', pointerMove, { passive: false });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('pointermove', pointerMove, { passive: false }));
        ['pointerup','pointercancel','pointerleave','pointerout'].forEach(evt => {
          pagesContainer.addEventListener(evt, endDrag, { passive: true });
          this._listenerCleanups.push(() => pagesContainer.removeEventListener(evt, endDrag, { passive: true }));
        });

        // Fallback touch events (older browsers / if pointer events not supported)
        if (!window.PointerEvent) {
          const touchStart = (e) => {
            if (e.touches.length === 1) {
              const t = e.touches[0];
              beginDrag(t.clientX);
            }
          };
          pagesContainer.addEventListener('touchstart', touchStart, { passive: true });
          this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchstart', touchStart, { passive: true }));
          const touchMove = (e) => {
            if (e.touches.length !== 1 || !dragging) return;
            const t = e.touches[0];
            moveDrag(t.clientX, () => e.preventDefault());
          };
          pagesContainer.addEventListener('touchmove', touchMove, { passive: false });
          this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchmove', touchMove, { passive: false }));
          pagesContainer.addEventListener('touchend', endDrag, { passive: true });
          this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchend', endDrag, { passive: true }));
          pagesContainer.addEventListener('touchcancel', endDrag, { passive: true });
          this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchcancel', endDrag, { passive: true }));
        }
      }
    } else {
      if (this.enableMobileMomentum) {
        this._mode = 'mobile-momentum';
        if (previousMode !== this._mode) prevVelocity = 0;
        const iv = Math.max(Math.min(prevVelocity, MOBILE_MOMENTUM_CONFIG.maxVelocity), -MOBILE_MOMENTUM_CONFIG.maxVelocity);
        this.momentum = createMomentumAxis({
          getPos: () => pagesContainer.scrollTop,
          setPos: v => { pagesContainer.scrollTop = v; },
          getMax: () => pagesContainer.scrollHeight - pagesContainer.clientHeight,
          friction: MOBILE_MOMENTUM_CONFIG.friction,
          acceleration: MOBILE_MOMENTUM_CONFIG.acceleration,
          maxVelocity: MOBILE_MOMENTUM_CONFIG.maxVelocity,
          minVelocity: MOBILE_MOMENTUM_CONFIG.minVelocity,
          initialVelocity: iv
        });
        const wheelHandlerMobile = (e) => {
          if (e.deltaY !== 0) {
            e.preventDefault();
            if (this.momentum && typeof this.momentum.push === 'function') {
              this.momentum.push(e.deltaY);
            }
          }
        };
        pagesContainer.addEventListener('wheel', wheelHandlerMobile, { passive: false });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('wheel', wheelHandlerMobile, { passive: false }));
        // Touch momentum
  let lastY = 0; // previous touch Y coordinate for delta calculation
  let isTouching = false; // track active touch session
        const touchStartMomentum = (e) => {
          if (e.touches.length === 1) {
            isTouching = true;
            lastY = e.touches[0].clientY;
          }
        };
        pagesContainer.addEventListener('touchstart', touchStartMomentum, { passive: true });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchstart', touchStartMomentum, { passive: true }));
        const touchMoveMomentum = (e) => {
          if (!isTouching || e.touches.length !== 1) return;
          const y = e.touches[0].clientY;
          const delta = lastY - y;
          if (this.momentum && typeof this.momentum.push === 'function') {
            this.momentum.push(delta);
          }
          lastY = y;
        };
        pagesContainer.addEventListener('touchmove', touchMoveMomentum, { passive: true });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchmove', touchMoveMomentum, { passive: true }));
        const touchEndMomentum = () => { isTouching = false; };
        pagesContainer.addEventListener('touchend', touchEndMomentum, { passive: true });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchend', touchEndMomentum, { passive: true }));
        pagesContainer.addEventListener('touchcancel', touchEndMomentum, { passive: true });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('touchcancel', touchEndMomentum, { passive: true }));
      } else {
        this._mode = 'mobile-native';
        this.momentum = null; // native scroll only
        // Ensure wheel scrolling works even if body is fixed / overflow hidden (map wheel delta directly)
        const WHEEL_SCALE = 0.35; // reduce vertical movement per wheel tick
        // Aggregate wheel deltas within a frame to eliminate micro alternating jitter from high-res devices
        let wheelAccum = 0;
        let rafScheduled = false;
        const flushWheel = () => {
          if (wheelAccum !== 0) {
            const prev = pagesContainer.scrollTop;
            pagesContainer.scrollTop += wheelAccum * WHEEL_SCALE;
            // Prevent rubberband default if we hit an edge
            // (no event to call preventDefault on here; edge bounce already mitigated by body fixed)
            wheelAccum = 0;
          }
          rafScheduled = false;
        };
        const nativeWheel = (e) => {
          wheelAccum += e.deltaY;
          if (!rafScheduled) {
            rafScheduled = true;
            requestAnimationFrame(flushWheel);
          }
          // Always prevent default so the page itself never scrolls / bounces
          e.preventDefault();
        };
        pagesContainer.addEventListener('wheel', nativeWheel, { passive: false });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('wheel', nativeWheel, { passive: false }));

        // Add pointer (mouse) drag support for vertical scroll when in mobile-native and using a non-touch pointer
        let dragging = false;
        let startY = 0;
        let startScrollTop = 0;
        const pointerDownNative = (e) => {
          if (e.isPrimary && e.pointerType === 'mouse') {
            dragging = true;
            startY = e.clientY;
            startScrollTop = pagesContainer.scrollTop;
          }
        };
        pagesContainer.addEventListener('pointerdown', pointerDownNative, { passive: true });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('pointerdown', pointerDownNative, { passive: true }));
        const DRAG_SCALE = 0.55; // scale pixel drag distance -> scroll distance
        const pointerMoveNative = (e) => {
          if (!dragging || e.pointerType !== 'mouse') return;
          const delta = startY - e.clientY; // drag down -> scroll down
          pagesContainer.scrollTop = startScrollTop + delta * DRAG_SCALE;
          e.preventDefault();
        };
        pagesContainer.addEventListener('pointermove', pointerMoveNative, { passive: false });
        this._listenerCleanups.push(() => pagesContainer.removeEventListener('pointermove', pointerMoveNative, { passive: false }));
        const endDrag = () => { dragging = false; };
        ['pointerup','pointercancel','pointerleave','pointerout'].forEach(evt => {
          pagesContainer.addEventListener(evt, endDrag, { passive: true });
          this._listenerCleanups.push(() => pagesContainer.removeEventListener(evt, endDrag, { passive: true }));
        });
      }
    }

    // Optional: respond to viewport changes (simple re-init approach)
    desktopQuery.addEventListener('change', () => {
      const idx = this.getCurrentPageIndex();
      // Debounce re-init into next frame to batch with resize if both fire
      requestAnimationFrame(() => this.init(onFrame, { preserveIndex: idx }));
    });

    // Bind a resize listener once to preserve momentum across same-mode resizes.
    if (!this._resizeBound) {
      let resizeScheduled = false;
      window.addEventListener('resize', () => {
        if (resizeScheduled) return;
        resizeScheduled = true;
        const idx = this.getCurrentPageIndex();
        requestAnimationFrame(() => {
          resizeScheduled = false;
          this.init(onFrame, { preserveIndex: idx });
        });
      });
      this._resizeBound = true;
    }

    // After mode establishment & listeners, restore page index if provided
    if (preserveIndex !== null) {
      this.scrollToPageIndex(preserveIndex);
    }
    // Normalize images after layout stabilized
    this._scheduleNormalize();
    this._initInProgress = false;
  },
  getScrollPos: function() {
    return this.momentum ? this.momentum.getPos() : 0;
  },
  getScrollVelocity: function() {
    return this.momentum ? this.momentum.getVelocity() : 0;
  },
  setMobileMomentumEnabled: function(enabled) {
    this.enableMobileMomentum = !!enabled;
    // Re-init to apply change
    this.init();
  }
};

export default scroll;